import { Flex } from 'antd';
import { useMemo, useState } from 'react';

import type {
  Entities_Login_StateInfo_Response,
  Entities_Player_ActivateFamilyLink_Response,
} from '@suntex.fim/api-client';
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { LoginType } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';
import { Button, LoaderOverlay, Modal, showErrorMessage } from '@suntex.fim/ui';

import * as images from '../../assets/family-link/images';
import { usePlayerContext } from '../../context';
import '../../style/family-link.scss';

import { FamilyLinkBadges } from './FamilyLinkBadges';
import { FamilyLinkBanner } from './FamilyLinkBanner';
import { FamilyLinkStats } from './FamilyLinkStats';

export function FamilyLink() {
  const { login } = useAuth(isAuthenticated);

  if (login.loginType === LoginType.IndividualPlayer) return null;

  return <FamilyLinkContent login={login} />;
}

function FamilyLinkContent({
  login,
}: {
  login: Entities_Login_StateInfo_Response;
}) {
  const { api } = useFramework();
  const [showPassword, setShowPassword] = useState(false);
  const { playerInfo, reload: reloadPlayerInfo } = usePlayerContext();
  const familyLinkData = playerInfo?.family;
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activateFamilyLink, setActivateFamilyLink] =
    useState<Entities_Player_ActivateFamilyLink_Response>();
  const { playerId, id } = login;

  const generateFamilyLink = async () => {
    try {
      setIsLoading(true);
      const response = await api.post_Player_ActivateFamilyLink({
        playerId,
        loginId: id,
      });
      setActivateFamilyLink(response);
      setIsModalOpen(true);
    } catch (error) {
      showErrorMessage('Failed to generate Family Link', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsLoading(true);
    reloadPlayerInfo()
      .catch((error: unknown) => {
        showErrorMessage('Failed to fetch player info', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const colProps = useMemo(
    () => ({
      xs: { flex: '100%' },
      sm: { flex: '50%' },
      md: { flex: '50%' },
      xl: { flex: '50%' },
    }),
    [],
  );

  const handlePasswordClick = () => setShowPassword((prev) => !prev);

  if (isLoading) return <LoaderOverlay isLoading={isLoading} />;

  return (
    <div className="familylink">
      <h3 className="familylink__head">Family Link</h3>
      <Flex gap={30} className="familylink__wrapper" wrap>
        <Flex vertical gap={20} className="familylink__contentsection">
          {!familyLinkData ? (
            <div className="familylink__content">
              <h3>My Family Link Detail</h3>
              <p className="familylink-text">
                It seems you haven&apos;t added any family members yet. Please
                invite family members by sharing the Family link with them.
              </p>
              <Button
                size="small"
                onClick={() => {
                  void generateFamilyLink();
                }}
              >
                <images.ShareIcon />
                Share Family Link
              </Button>
            </div>
          ) : (
            <Flex
              gap={20}
              vertical
              className="familylink__content main-dashboard"
            >
              <h3>My Family Link Detail</h3>
              <FamilyLinkStats
                familyLinkData={familyLinkData}
                showPassword={showPassword}
                onPasswordClick={handlePasswordClick}
                colProps={colProps}
              />
            </Flex>
          )}
          <Flex vertical className="familylink__content">
            <h3>How does Family Link work?</h3>
            <p className="familylink-text">
              FamilyLink feature helps create a home connection.
              <br /> For each student enrolled in the program, we automatically
              create one additional First In Math User ID for a family member to
              use to play along with their child!
            </p>
            <FamilyLinkBadges />
          </Flex>
        </Flex>
        <FamilyLinkBanner />
      </Flex>
      {activateFamilyLink && (
        <Modal
          title="Family Link Created"
          closable={false}
          open={isModalOpen}
          onOk={handleModalClose}
          footer={[
            <Button key="ok" onClick={handleModalClose}>
              Ok
            </Button>,
          ]}
          width={450}
          className="family-link-modal"
        >
          <h5 className="fw-bold">
            Your FamilyLink® account has been created!
          </h5>
          <Flex vertical className="family-link-details">
            <span>Username: {activateFamilyLink.familyLinkUserName}</span>
            <span>Password: {activateFamilyLink.familyLinkPassword}</span>
          </Flex>
        </Modal>
      )}
    </div>
  );
}
